/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Theme, darken, lighten, makeStyles } from '@material-ui/core/styles';

import { ICtcaeSelectorRenderProps, ICtcaeSelectorProps as INgtCtcaeSelectorProps, CtcaeSelector as NgtCtcaeSelector } from '@ngt/react-ctcae';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { FieldRenderProps, useField } from 'react-final-form';
import { pascalToCamelCase } from '../OpmsInput';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export const TYPE_SELECTOR = 'Selector';
export const TYPE_SELECTOR_WITH_NAME = 'SelectorWithName';

type CtcaeSelectorType = typeof TYPE_SELECTOR | typeof TYPE_SELECTOR_WITH_NAME;

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type CtcaeSelectorsProps = Partial<Omit<INgtCtcaeSelectorProps, 'ctcaeUrl' | 'ctcaeVersion' | 'render' | 'gradeId' | 'termId' | 'specification' | 'onChange' | 'onBlur' | 'onFocus' >> & FieldRenderProps<any, any>

interface ICtcaeSelectorProps extends CtcaeSelectorsProps {
    display?: CtcaeSelectorType;
    gradeFieldName: string;
    specificationFieldName: string;
    onChange?: (ctcaeState: ICtcaeSelectorRenderProps['state']) => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


const useCtcaeDisplayWithNameStyles = makeStyles<Theme>(theme => ({
    container: {
        display: 'flex',
        alignItems: 'center'
    },
    name: {
        flex: '1 1 auto',
        marginRight: theme.spacing(2)
    },
    selector: {
        minWidth: 240,
        maxWidth: 240
    }
}));

const useCtcaeDisplayStyles = makeStyles<Theme>(theme => {
    const getColor = theme.palette.type === 'light' ? darken : lighten;
    const getBackgroundColor = theme.palette.type === 'light' ? lighten : darken;
    const getBorderColor = theme.palette.type === 'light' ? lighten : darken;

    return {
        container: {
            display: 'flex'
        },
        value: {
            flex: '1 1 auto'
        },
        info: {
            curosr: 'help',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: theme.spacing(0, 1),
            color: getColor(theme.palette.info.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: getBorderColor(theme.palette.info.main, 0.8),
            '& > *': {
                color: theme.palette.info.main,
            }
        },
        remove: {
            minWidth: 'auto',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: theme.spacing(0, 1),
            color: getColor(theme.palette.error.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: getBorderColor(theme.palette.error.main, 0.8),
            '& > *': {
                color: theme.palette.error.main,
            }
        },
        error: {
            color: theme.palette.error.main
        },
        infoTitle: {
            fontWeight: 'bold'
        },
        tooltip: {
            padding: theme.spacing(0),
            color: getColor(theme.palette.info.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: getBorderColor(theme.palette.info.main, 0.8),
        },
        infoSection: {
            padding: theme.spacing(1),
            color: theme.palette.info.main,
            borderBottomStyle: 'solid',
            borderBottomWidth: 1,
            borderBottomColor: getBorderColor(theme.palette.info.main, 0.8),

            '&:last-child': {
                borderBottom: 'none'
            }
        }
    }
});

const useCtcaeDisplayTooltipStyles = makeStyles<Theme>(theme => {
    const getColor = theme.palette.type === 'light' ? darken : lighten;
    const getBackgroundColor = theme.palette.type === 'light' ? lighten : darken;
    const getBorderColor = theme.palette.type === 'light' ? lighten : darken;

    return {
        tooltip: {
            padding: theme.spacing(0),
            color: getColor(theme.palette.info.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: getBorderColor(theme.palette.info.main, 0.8),
        }
    }
});


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const CtcaeDisplayWithName: React.FunctionComponent<ICtcaeSelectorRenderProps> = ({
    error,
    loading,
    open,
    isOpen,
    targetTermId,
    state,
    setCtcaeState,
    disabled,
    onBlur,
    onFocus,
    close
}) => {
    const classes = useCtcaeDisplayWithNameStyles();

    const termName = React.useMemo(() => {
        if (state?.selection?.term) {
            if (state.selection.term.specifyOther && state.selection.specification) {
                return `${state.selection.term.name} (${state.selection.specification})`
            }

            return state.selection.term.name
        }

        return 'Adverse Event'
    }, [state])

    return (
        // Displayed as a flex with width 100%.
        <div
            className={classes.container}
        >
            { /* flex: 1 1 auto; so that it takes all remaining space */}
            <div
                className={classes.name}
            >
                <Typography>
                    {termName}
                </Typography>
            </div>
            { /* set a perdetermined min / max width on this div, as it only needs to be able to display the static state text the CTCAE display shows, the rest of the space should go to showing the term name */}
            <div
                className={classes.selector}
            >
                <CtcaeDisplay
                    error={error}
                    loading={loading}
                    targetTermId={targetTermId}
                    state={state}
                    setCtcaeState={setCtcaeState}
                    disabled={disabled}
                    open={open}
                    onBlur={onBlur}
                    onFocus={onFocus} 
                    isOpen={isOpen} 
                    close={close}                
                />
            </div>
        </div>
    )
};

const CtcaeDisplay: React.FunctionComponent<ICtcaeSelectorRenderProps> = ({
    error,
    loading,
    open,
    targetTermId,
    state,
    setCtcaeState,
    onBlur,
    onFocus,
    disabled
}) => {
    const classes = useCtcaeDisplayStyles();
    const tooltipClasses = useCtcaeDisplayTooltipStyles();

    const valueText = React.useMemo(() => {
        if (loading) {
            return 'Loading...'
        }

        if (error) {
            return 'An Error Has Occurred'
        }

        if (state?.selection?.grade) {
            return `Grade ${state.selection.grade.name}`;
        }

        if (state?.selection?.term) {
            if (disabled) {
                return 'No Grade Selected'
            }

            return 'Select a Grade';
        }

        if (disabled) {
            return 'No CTCAE Selected';
        }

        return 'Select CTCAE'
    }, [disabled, state, loading, error]);

    const onKeyPress = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        event.preventDefault();

        if (event.charCode === 32) {
            open();
        }
    }, [open]);

    const onClick = React.useCallback(() => {
        if (!loading) {
            open();
        }
    }, [open, loading]);

    const remove = React.useCallback(() => {
        setCtcaeState(null);
    }, [setCtcaeState])

    return (
        // Displayed as a flex with width 100%.
        <div
            className={classes.container}
        >
            { /* flex: 1 1 auto; so that it takes all remaining space */}
            <div
                className={classes.value}
            >
                {
                    /* 
                     * shows text based on current state, typing disabled by making text field readonly ALWAYS.
                     * clicking text field while not loading or disabled calls open (which opens the CTCAE browser).
                     * hitting space while not disabled does the same as clicking (when text field is focused, still possible while read only, not possible while disabled)
                    */
                }
                <TextField
                    value={valueText}
                    onKeyPress={onKeyPress}
                    fullWidth={true}
                    inputProps={{
                        readOnly: true,
                        onFocus,
                        onBlur
                    }}
                    InputProps={{
                        startAdornment: loading || error ?
                            <InputAdornment position="start">
                                {
                                    loading && !error && (
                                        <CircularProgress size={16} />
                                    )
                                }
                                {
                                    error && (
                                        <FontAwesomeIcon className={classes.error} fixedWidth icon={faExclamationCircle} />
                                    )
                                }
                            </InputAdornment> :
                            undefined,
                        endAdornment: !disabled && !loading ?
                            <InputAdornment position="end">
                                <FontAwesomeIcon fixedWidth icon={faChevronDown} />
                            </InputAdornment> :
                            undefined
                    }}
                    onClick={onClick}
                    disabled={disabled}
                />
            </div>
            { /* remove icon, on click removes state. */}
            {
                !disabled && state?.selection?.term && (!targetTermId || state?.selection?.grade) && (
                    <Button
                        className={classes.remove}
                        onClick={remove}
                    >
                        <FontAwesomeIcon fixedWidth icon={faTimes} />
                    </Button>
                )
            }
            { /* info icon, on mouse over shows info about selected term */}
            {
                state?.selection?.term && (
                    <Tooltip
                        classes={tooltipClasses}
                        title={
                            <>
                                {
                                    state?.selection?.category && (
                                        <div
                                            className={classes.infoSection}
                                        >
                                            <div>
                                                <Typography
                                                    variant="subtitle1"
                                                >
                                                    Category
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography
                                                    variant="subtitle2"
                                                >
                                                    {state.selection.category.name}
                                                </Typography>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    state?.selection?.term && (
                                        <div
                                            className={classes.infoSection}
                                        >
                                            <div>
                                                <Typography
                                                    variant="subtitle1"
                                                >
                                                    Term
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography
                                                    variant="subtitle2"
                                                >
                                                    {state.selection.term.name}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography
                                                    variant="caption"
                                                >
                                                    {state.selection.term.description}
                                                </Typography>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    state?.selection?.subterm && (
                                        <div
                                            className={classes.infoSection}
                                        >
                                            <div>
                                                <Typography
                                                    variant="subtitle1"
                                                >
                                                    Subterm
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography
                                                    variant="subtitle2"
                                                >
                                                    {state.selection.subterm.name}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography
                                                    variant="caption"
                                                >
                                                    {state.selection.subterm.description}
                                                </Typography>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    state?.selection?.grade && (
                                        <div
                                            className={classes.infoSection}
                                        >
                                            <div>
                                                <Typography
                                                    variant="subtitle1"
                                                >
                                                    Grade
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography
                                                    variant="subtitle2"
                                                >
                                                    Grade {state.selection.grade.name}
                                                </Typography>
                                            </div>
                                            <div>
                                                <Typography
                                                    variant="caption"
                                                >
                                                    {state.selection.grade.description}
                                                </Typography>
                                            </div>
                                        </div>
                                    )
                                }
                            </>
                        }
                    >
                        <div
                            className={classes.info}
                        >
                            <FontAwesomeIcon fixedWidth icon={faInfoCircle} />
                        </div>
                    </Tooltip>
                )
            }
        </div>
    )
};

const CtcaeSelector: React.FunctionComponent<ICtcaeSelectorProps> = ({
    display,
    input: { name, value, onChange: onInputChange, onFocus, onBlur },
    meta,
    onChange,
    gradeFieldName,
    specificationFieldName,
    render,
    ...rest
}) => {

    const parentName = React.useMemo(() => {
        return name.split('.').filter((namePart, index, nameParts) => index !== nameParts.length - 1).join('.');
    }, [name]);

    const completeGradeFieldName = React.useMemo(() => {
        return `${parentName}.${pascalToCamelCase(gradeFieldName)}`;
    }, [gradeFieldName, parentName]);

    const completeSpecificationFieldName = React.useMemo(() => {
        return `${parentName}.${pascalToCamelCase(specificationFieldName)}`;
    }, [specificationFieldName, parentName]);

    const { input: { value: gradeValue, onChange: onGradeChange } } = useField<number | null | undefined>(completeGradeFieldName, { subscription: { value: true } });
    const { input: { value: specificationValue, onChange: onSpecificationChange } } = useField<number | null | undefined>(completeSpecificationFieldName, { subscription: { value: true } });

    const component = display === TYPE_SELECTOR_WITH_NAME ? CtcaeDisplayWithName : CtcaeDisplay;


    const onChangeCombined = React.useCallback((state: ICtcaeSelectorRenderProps['state']) => {
        if (onChange) {
            onChange(state);
        }

        if (state?.selection?.grade?.id) {
            onGradeChange(state.selection.grade?.id);
        }
        else {
            onGradeChange(null);
        }

        if (state?.selection?.term?.id) {
            onInputChange(state.selection.term?.id);
        }
        else {
            onInputChange(null);
        }

        if (state?.selection?.specification) {
            onSpecificationChange(state.selection.specification);
        }
        else {
            onSpecificationChange(null);
        }


    }, [onInputChange, onChange, onGradeChange, onSpecificationChange])

    return (
        <NgtCtcaeSelector
            ctcaeUrl="https://ctcae.breastcancertrials.org.au"
            ctcaeVersion="5.0"
            render={component}
            termId={value}
            gradeId={gradeValue}
            specification={specificationValue}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={onChangeCombined}
            {...rest as any}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default CtcaeSelector;
